import React, { Component } from 'react';
import * as Http from '../../../http';
import { t, onLangChange } from '../../../i18n';
import Loading from '../../../components/Loading';
import ResultTable from '../CapMrCor/ResultTable';
import RarocCrResultTable from '../CrResultTable';
import '../CapMrCor/cap-mr-cor.style.scss';
import '../CapMrCor/raroc-theme.style.scss';

const CHUNK_SIZE = 5 * 1024 * 1024;
const todayISO = () => new Date().toISOString().slice(0, 10);

const FLOW_VERIFY = 'verify';
const FLOW_ANALYTICS = 'analytics';

const VERIFY_IDLE = 'idle';
const VERIFY_UPLOADING = 'uploading';
const VERIFY_VALIDATING = 'validating';
const VERIFY_PASSED = 'passed';
const VERIFY_FAILED_VALIDATION = 'failed-validation';
const VERIFY_FAILED_UPLOAD = 'failed-upload';

const ANALYTICS_IDLE = 'idle';
const ANALYTICS_RUNNING = 'running';
const ANALYTICS_DONE = 'done';

const CSV_EXTS = ['csv', 'txt'];
const EXCEL_EXTS = ['xlsx', 'xls'];
const ACCEPT = '.xlsx,.xls,.csv,.txt';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const extOf = (name) => (name.split('.').pop() || '').toLowerCase();
const day = (d) => String(d || '').slice(0, 10);
const fmtSize = (n) => (n == null ? '' : n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`);
const DATASET_PREFIX = 'RAROC_';

const ensureDatasetPrefix = (rawName = '') => {
    const clean = String(rawName || '').trim();
    if (!clean) return '';
    if (clean.toUpperCase().startsWith(DATASET_PREFIX)) return clean;
    return `${DATASET_PREFIX}${clean}`;
};

const SECTION_CONFIGS = [
    {
        id: 'capMrCor',
        title: 'STG_MR & COR CAP',
        tag: 'CAP',
        module: 'cap-mr-cor',
        reportPrefix: 'STG_MR_COR_CAP',
        sheets: ['IN_CAP_MR', 'IN_CAP_COR', 'IN_ICAAP_MR'],
        uploadComplete: Http.uploadCapMrCorComplete,
        renderResult: (data) => <ResultTable result={data} />,
    },
    {
        id: 'rarocCr',
        title: 'RAROC CR',
        tag: 'CR',
        module: 'raroc-cr',
        reportPrefix: 'RAROC_CR',
        sheets: [
            'IN_TB',
            'IN_TOI_BRANCH',
            'IN_TOI_PRODUCT',
            'IN_TOI_CUSTOMER',
            'IN_TOI_UNIT',
            'IN_TOI_CHANNEL',
            'IN_COMMITMENT',
            'IN_LOAN_CONTRACT',
            'IN_CARD',
            'IN_INVESTMENT',
            'IN_MM_CONTRACTS',
            'IN_DEPOSIT_BORROWING',
            'IN_ICAAP_RESULT',
            'IN_CAP_COR_BANKING_GROUP',
            'IN_CAP_COR_BANKING_CUS',
            'IN_ICAAP_CR',
            'IN_CUSTOMER_MASTER',
            'IN_DIM_BRANCH',
            'IN_DIM_PRODUCT',
            'IN_DIM_CUSTOMER_TYPE',
            'IN_DIM_INDUSTRY',
            'IN_DIM_RM',
            'IN_CHANNEL_CODE_CD',
            'IN_UNIT_CODE_CD',
            'IN_CAP_OR',
        ],
        uploadComplete: Http.uploadRarocCrComplete,
        renderResult: (data) => <RarocCrResultTable result={data} stgCcr={data && data.stg_ccr} />,
    },
];

const defaultDatasetName = () => {
    const n = new Date();
    const p = (x) => ('0' + x).slice(-2);
    return ensureDatasetPrefix(`${n.getFullYear()}${p(n.getMonth() + 1)}${p(n.getDate())}_${p(n.getHours())}${p(n.getMinutes())}${p(n.getSeconds())}`);
};

const initSectionsState = () =>
    SECTION_CONFIGS.reduce((acc, section) => {
        acc[section.id] = {
            file: null,
            csvFiles: {},
            unassigned: [],
            dragOver: false,
        };
        return acc;
    }, {});

const sheetFromName = (name, sheets) => {
    const base = String(name)
        .replace(/\.[^.]+$/, '')
        .toUpperCase();
    return sheets.find((s) => base === s) || sheets.find((s) => base.includes(s)) || null;
};

const findBestSectionSheetMatch = (name) => {
    const base = String(name)
        .replace(/\.[^.]+$/, '')
        .toUpperCase();

    const candidates = [];
    SECTION_CONFIGS.forEach((section) => {
        section.sheets.forEach((sheet) => {
            const upperSheet = String(sheet).toUpperCase();
            if (base === upperSheet) {
                candidates.push({ sectionId: section.id, sheet, score: 10000 + upperSheet.length });
            } else if (base.includes(upperSheet)) {
                // Prefer more specific (longer) sheet names for fuzzy matches.
                candidates.push({ sectionId: section.id, sheet, score: upperSheet.length });
            }
        });
    });

    if (!candidates.length) return null;
    candidates.sort((a, b) => b.score - a.score);
    return candidates[0];
};

const toReadableErrorDetail = (raw) => {
    if (raw == null) return '';
    if (typeof raw === 'string') return raw;
    if (Array.isArray(raw)) {
        return raw
            .slice(0, 5)
            .map((item) => toReadableErrorDetail(item))
            .filter(Boolean)
            .join(' | ');
    }
    if (typeof raw === 'object') {
        const message = typeof raw.message === 'string' ? raw.message : '';
        const errors = Array.isArray(raw.errors) ? raw.errors : [];

        if (errors.length) {
            const details = errors.slice(0, 5).map((err) => {
                if (typeof err === 'string') return err;
                if (!err || typeof err !== 'object') return String(err);

                const loc = [];
                if (err.file) loc.push(err.file);
                if (err.row != null && err.row !== '') loc.push(`row ${err.row}`);
                if (err.column) loc.push(`col ${err.column}`);

                const errMsg = err.message || err.reason || err.expected || 'Invalid data';
                return `${loc.length ? `[${loc.join(', ')}] ` : ''}${errMsg}`;
            });

            const more = errors.length > 5 ? `(+${errors.length - 5} more)` : '';
            return [message, details.join(' | '), more].filter(Boolean).join(' | ');
        }

        if (message) return message;

        try {
            return JSON.stringify(raw);
        } catch (e) {
            return String(raw);
        }
    }
    return String(raw);
};

const summarizeValidationErrors = (raw) => {
    const errors = raw && Array.isArray(raw.errors) ? raw.errors : [];
    if (!errors.length) return null;

    const summary = errors.reduce(
        (acc, err) => {
            const message = String((err && err.message) || '').toLowerCase();
            if (message.includes('thiếu cột') || message.includes('missing')) acc.missingFields += 1;
            else if (message.includes('không đúng kiểu') || message.includes('format') || message.includes('invalid')) acc.wrongFormat += 1;
            else acc.other += 1;
            return acc;
        },
        { total: errors.length, missingFields: 0, wrongFormat: 0, other: 0 },
    );

    return summary;
};

export default class UploadFile extends Component {
    uploadInFlight = false;

    state = {
        flow: FLOW_VERIFY,
        reportDate: todayISO(),
        datasetName: defaultDatasetName(),
        sections: initSectionsState(),
        loading: false,
        progress: 0,
        verifyPhase: VERIFY_IDLE,
        analyticsPhase: ANALYTICS_IDLE,
        analyticsProgress: 0,
        processVisible: false,
        verifySummary: null,
        results: {},
        message: null,
        error: null,
        _lang: null,
        downloadingSample: false,
    };

    componentDidMount() {
        this._unsub = onLangChange(() => this.setState({ _lang: Date.now() }));
    }

    componentDidUpdate(prevProps, prevState) {
        const reachedSuccess = prevState.analyticsPhase !== ANALYTICS_DONE && this.state.analyticsPhase === ANALYTICS_DONE;
        const switchedToAnalytics = prevState.flow !== FLOW_ANALYTICS && this.state.flow === FLOW_ANALYTICS;
        const gotResults = Object.keys(prevState.results || {}).length === 0 && Object.keys(this.state.results || {}).length > 0;
        if (reachedSuccess) {
            this.scrollToTop();
            setTimeout(this.scrollToTop, 140);
        }
        if (switchedToAnalytics || gotResults) {
            this.scrollToTopImmediate();
            setTimeout(this.scrollToTopImmediate, 60);
        }
    }

    componentWillUnmount() {
        this._unsub && this._unsub();
    }

    sectionConfig = (sectionId) => SECTION_CONFIGS.find((s) => s.id === sectionId);

    // RAROC là MỘT chức năng: CAP và CR dùng chung 1 report_name = RAROC_<suffix>
    // → input/output của cả dataset nằm chung 1 nơi (cùng thư mục, cùng dataset).
    buildReportName = () => ensureDatasetPrefix(this.state.datasetName);

    getSectionMode = (sectionId) => (this.state.sections[sectionId].file ? 'excel' : 'csv');

    isSectionReady = (sectionId) => {
        const section = this.sectionConfig(sectionId);
        const st = this.state.sections[sectionId];
        if (!section || !st) return false;
        if (st.file) return true;
        return section.sheets.every((sheet) => !!st.csvFiles[sheet]);
    };

    getTotalUploadFileCount = () =>
        SECTION_CONFIGS.reduce((count, section) => {
            const st = this.state.sections[section.id];
            return count + (st.file ? 1 : section.sheets.length);
        }, 0);

    setSectionState = (sectionId, updater) => {
        this.setState((prev) => {
            const current = prev.sections[sectionId];
            const patch = typeof updater === 'function' ? updater(current) : updater;
            return {
                sections: {
                    ...prev.sections,
                    [sectionId]: {
                        ...current,
                        ...patch,
                    },
                },
            };
        });
    };

    onPickSectionFiles = (sectionId, e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length) this.addFilesToSection(sectionId, files);
    };

    onPickAllFiles = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length) this.addFilesToAllSections(files);
    };

    onDropSection = (sectionId, e) => {
        e.preventDefault();
        this.setSectionState(sectionId, { dragOver: false });
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) this.addFilesToSection(sectionId, e.dataTransfer.files);
    };

    onDropAllFiles = (e) => {
        e.preventDefault();
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
            this.addFilesToAllSections(Array.from(e.dataTransfer.files));
        }
    };

    onDragOverSection = (sectionId, e) => {
        e.preventDefault();
        if (!this.state.sections[sectionId].dragOver) this.setSectionState(sectionId, { dragOver: true });
    };

    onDragLeaveSection = (sectionId, e) => {
        e.preventDefault();
        if (this.state.sections[sectionId].dragOver) this.setSectionState(sectionId, { dragOver: false });
    };

    addFilesToSection = (sectionId, fileList) => {
        const section = this.sectionConfig(sectionId);
        if (!section) return;

        let excel = null;
        const matched = {};
        const unassigned = [];
        const rejected = [];

        Array.from(fileList).forEach((f) => {
            const ext = extOf(f.name);
            if (EXCEL_EXTS.includes(ext)) {
                excel = f;
            } else if (CSV_EXTS.includes(ext)) {
                const sheet = sheetFromName(f.name, section.sheets);
                if (sheet) matched[sheet] = f;
                else unassigned.push(f);
            } else {
                rejected.push(f.name);
            }
        });

        this.setState((prev) => {
            const current = prev.sections[sectionId];
            const nextSection = {
                ...current,
                dragOver: false,
            };

            if (excel) {
                nextSection.file = excel;
                nextSection.csvFiles = {};
                nextSection.unassigned = [];
            } else if (Object.keys(matched).length || unassigned.length) {
                nextSection.file = null;
                nextSection.csvFiles = { ...current.csvFiles, ...matched };
                nextSection.unassigned = [...current.unassigned, ...unassigned];
            }

            return {
                sections: {
                    ...prev.sections,
                    [sectionId]: nextSection,
                },
                flow: FLOW_VERIFY,
                verifyPhase: VERIFY_IDLE,
                analyticsPhase: ANALYTICS_IDLE,
                analyticsProgress: 0,
                processVisible: false,
                verifySummary: null,
                results: {},
                message: excel && (Object.keys(matched).length || unassigned.length) ? t('rstg.msg.excelPriority').replace('{title}', section.title) : null,
                error: rejected.length ? `${t('rstg.err.badExt')}: ${rejected.join(', ')}` : null,
            };
        });
    };

    addFilesToAllSections = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        this.setState((prev) => {
            const nextSections = { ...prev.sections };
            const rejected = [];

            SECTION_CONFIGS.forEach((section) => {
                nextSections[section.id] = {
                    ...nextSections[section.id],
                    dragOver: false,
                };
            });

            files.forEach((f) => {
                const ext = extOf(f.name);
                if (EXCEL_EXTS.includes(ext)) {
                    // Best-effort routing: excel tên có CR -> RAROC CR, còn lại vào CAP MR/COR.
                    const isCrExcel = /RAROC[_\s-]*CR|\bCR\b/i.test(f.name);
                    const targetSectionId = isCrExcel ? 'rarocCr' : 'capMrCor';
                    nextSections[targetSectionId] = {
                        ...nextSections[targetSectionId],
                        file: f,
                        csvFiles: {},
                        unassigned: [],
                    };
                    return;
                }

                if (CSV_EXTS.includes(ext)) {
                    const bestMatch = findBestSectionSheetMatch(f.name);
                    const matchedSectionId = bestMatch && bestMatch.sectionId;
                    const matchedSheet = bestMatch && bestMatch.sheet;

                    if (matchedSectionId && matchedSheet) {
                        nextSections[matchedSectionId] = {
                            ...nextSections[matchedSectionId],
                            file: null,
                            csvFiles: {
                                ...nextSections[matchedSectionId].csvFiles,
                                [matchedSheet]: f,
                            },
                        };
                    } else {
                        // Không nhận diện được thì gán vào unassigned của phần CR để user map thủ công.
                        nextSections.rarocCr = {
                            ...nextSections.rarocCr,
                            unassigned: [...nextSections.rarocCr.unassigned, f],
                        };
                    }
                    return;
                }

                rejected.push(f.name);
            });

            return {
                sections: nextSections,
                flow: FLOW_VERIFY,
                verifyPhase: VERIFY_IDLE,
                analyticsPhase: ANALYTICS_IDLE,
                analyticsProgress: 0,
                processVisible: false,
                verifySummary: null,
                results: {},
                message: null,
                error: rejected.length ? `${t('rstg.err.badExt')}: ${rejected.join(', ')}` : null,
            };
        });
    };

    onPickSlot = (sectionId, sheet, e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!CSV_EXTS.includes(extOf(file.name))) return this.setState({ error: t('rstg.err.badExtCsv') });

        this.setState((prev) => ({
            sections: {
                ...prev.sections,
                [sectionId]: {
                    ...prev.sections[sectionId],
                    file: null,
                    csvFiles: {
                        ...prev.sections[sectionId].csvFiles,
                        [sheet]: file,
                    },
                },
            },
            flow: FLOW_VERIFY,
            verifyPhase: VERIFY_IDLE,
            analyticsPhase: ANALYTICS_IDLE,
            analyticsProgress: 0,
            processVisible: false,
            verifySummary: null,
            results: {},
            message: null,
            error: null,
        }));
    };

    removeExcel = (sectionId) => {
        this.setSectionState(sectionId, {
            file: null,
            csvFiles: {},
            unassigned: [],
        });
        this.setState({ results: {}, processVisible: false, message: null, error: null });
    };

    removeSheet = (sectionId, sheet) => {
        this.setState((prev) => {
            const csvFiles = { ...prev.sections[sectionId].csvFiles };
            delete csvFiles[sheet];
            return {
                sections: {
                    ...prev.sections,
                    [sectionId]: {
                        ...prev.sections[sectionId],
                        csvFiles,
                    },
                },
                flow: FLOW_VERIFY,
                verifyPhase: VERIFY_IDLE,
                analyticsPhase: ANALYTICS_IDLE,
                analyticsProgress: 0,
                processVisible: false,
                verifySummary: null,
                results: {},
                message: null,
            };
        });
    };

    assignUnassigned = (sectionId, idx, sheet) => {
        if (!sheet) return;
        this.setState((prev) => {
            const unassigned = prev.sections[sectionId].unassigned.slice();
            const [f] = unassigned.splice(idx, 1);
            return {
                sections: {
                    ...prev.sections,
                    [sectionId]: {
                        ...prev.sections[sectionId],
                        csvFiles: {
                            ...prev.sections[sectionId].csvFiles,
                            [sheet]: f,
                        },
                        unassigned,
                    },
                },
                flow: FLOW_VERIFY,
                verifyPhase: VERIFY_IDLE,
                analyticsPhase: ANALYTICS_IDLE,
                analyticsProgress: 0,
                processVisible: false,
                verifySummary: null,
                results: {},
                message: null,
            };
        });
    };

    removeUnassigned = (sectionId, idx) => {
        this.setState((prev) => {
            const unassigned = prev.sections[sectionId].unassigned.slice();
            unassigned.splice(idx, 1);
            return {
                sections: {
                    ...prev.sections,
                    [sectionId]: {
                        ...prev.sections[sectionId],
                        unassigned,
                    },
                },
            };
        });
    };

    scrollToTop = () => {
        if (typeof window !== 'undefined') {
            if (typeof window.scrollTo === 'function') window.scrollTo({ top: 0, behavior: 'smooth' });
            const root = document.scrollingElement || document.documentElement || document.body;
            if (root) root.scrollTop = 0;
            ['.main-panel', '.main-content', '.content', '.container-fluid'].forEach((selector) => {
                const el = document.querySelector(selector);
                if (!el) return;
                if (typeof el.scrollTo === 'function') el.scrollTo({ top: 0, behavior: 'smooth' });
                el.scrollTop = 0;
            });
        }
    };

    scrollToTopImmediate = () => {
        if (typeof window === 'undefined') return;
        if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
        const root = document.scrollingElement || document.documentElement || document.body;
        if (root) root.scrollTop = 0;
        ['.main-panel', '.main-content', '.content', '.container-fluid'].forEach((selector) => {
            const el = document.querySelector(selector);
            if (el) el.scrollTop = 0;
        });
    };

    downloadSample = () => {
        this.setState({ downloadingSample: true });
        Http.downloadRarocSample().finally(() => this.setState({ downloadingSample: false }));
    };

    startNewUploadFlow = () => {
        this.setState({
            flow: FLOW_VERIFY,
            reportDate: todayISO(),
            datasetName: defaultDatasetName(),
            sections: initSectionsState(),
            loading: false,
            progress: 0,
            verifyPhase: VERIFY_IDLE,
            analyticsPhase: ANALYTICS_IDLE,
            analyticsProgress: 0,
            processVisible: false,
            verifySummary: null,
            results: {},
            message: null,
            error: null,
        });
    };

    uploadChunks = async (file, fileGuid, baseProgress, span) => {
        const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        let begin = 0;
        for (let i = 0; i < total; i++) {
            const chunk = file.slice(begin, begin + CHUNK_SIZE);
            const buffer = await chunk.arrayBuffer();
            const res = await Http.uploadFileChunkBinary(buffer, { id: i, fileName: fileGuid });
            if (!res || !res.Success) throw new Error(t('rstg.err.chunk'));
            begin += CHUNK_SIZE;
            this.setState({ progress: Math.round(baseProgress + ((i + 1) / total) * span) });
        }
    };

    uploadSection = async (section, perFileProgress, uploadedFilesCount) => {
        const sectionState = this.state.sections[section.id];
        const mode = sectionState.file ? 'excel' : 'csv';
        const reportName = this.buildReportName();
        const reportDate = this.state.reportDate;
        let res = null;
        let filesUploaded = uploadedFilesCount;

        if (mode === 'excel') {
            const file = sectionState.file;
            const fileGuid = `${Date.now()}_${section.id}_${file.name}`;
            await this.uploadChunks(file, fileGuid, filesUploaded * perFileProgress, perFileProgress);
            filesUploaded += 1;
            this.setState({ verifyPhase: VERIFY_VALIDATING });
            res = await section.uploadComplete({
                originFileName: file.name,
                fileGuid,
                reportDate,
                reportName,
                fileExtension: extOf(file.name),
                isAllFilesUploaded: 'true',
            });
            return { res, filesUploaded };
        }

        for (let i = 0; i < section.sheets.length; i++) {
            const sheet = section.sheets[i];
            const f = sectionState.csvFiles[sheet];
            const ext = extOf(f.name);
            const fileGuid = `${Date.now()}_${section.id}_${i}_${sheet}.${ext}`;
            await this.uploadChunks(f, fileGuid, filesUploaded * perFileProgress, perFileProgress);
            filesUploaded += 1;
            const isLast = i === section.sheets.length - 1;
            if (isLast) this.setState({ verifyPhase: VERIFY_VALIDATING });
            res = await section.uploadComplete({
                originFileName: `${sheet}.${ext}`,
                fileGuid,
                reportDate,
                reportName,
                fileExtension: ext,
                isAllFilesUploaded: isLast ? 'true' : 'false',
            });
            // Detect silent failures on intermediate files early.
            // Intermediate responses return { Success: true } for PENDING_UPLOAD.
            // Any other non-success is an unexpected server error — abort immediately.
            if (!isLast && res && !res.Success) {
                return { res, filesUploaded };
            }
        }

        return { res, filesUploaded };
    };

    onUpload = async () => {
        if (this.uploadInFlight) return;

        const { reportDate, datasetName } = this.state;
        const normalizedDatasetName = ensureDatasetPrefix(datasetName);

        if (!reportDate) return this.setState({ error: t('rstg.err.noDate') });
        if (!normalizedDatasetName) return this.setState({ error: t('rstg.err.noDataset') });

        if (normalizedDatasetName !== datasetName) {
            this.setState({ datasetName: normalizedDatasetName });
        }

        const unreadySection = SECTION_CONFIGS.find((section) => !this.isSectionReady(section.id));
        if (unreadySection) {
            return this.setState({
                error: t('rstg.err.missingSection').replace('{title}', unreadySection.title),
            });
        }

        // Khi user bấm Upload & Analyze, cuộn lên top ngay để nhìn thấy process bar tức thì.
        this.scrollToTopImmediate();

        this.setState(
            {
                loading: true,
                progress: 0,
                verifyPhase: VERIFY_UPLOADING,
                analyticsPhase: ANALYTICS_IDLE,
                analyticsProgress: 0,
                processVisible: true,
                verifySummary: null,
                results: {},
                message: null,
                error: null,
            },
            this.scrollToTopImmediate,
        );

        try {
            this.uploadInFlight = true;
            const totalFiles = this.getTotalUploadFileCount();
            const perFileProgress = totalFiles > 0 ? 90 / totalFiles : 90;
            let uploadedFilesCount = 0;
            const results = {};

            for (let s = 0; s < SECTION_CONFIGS.length; s++) {
                const section = SECTION_CONFIGS[s];
                const { res, filesUploaded } = await this.uploadSection(section, perFileProgress, uploadedFilesCount);
                uploadedFilesCount = filesUploaded;

                if (!res || !res.Success || !res.Data) {
                    const code = res && res.ErrorCode;
                    const raw = res && res.Data;
                    const rawDetail = toReadableErrorDetail(raw);
                    const verifySummary = summarizeValidationErrors(raw);
                    const isDup = Number(code) === 701 || /ExecuteTransaction|Duplicate/i.test(String(raw || ''));
                    this.setState({
                        loading: false,
                        verifyPhase: Number(code) === 718 ? VERIFY_FAILED_VALIDATION : VERIFY_FAILED_UPLOAD,
                        analyticsPhase: ANALYTICS_IDLE,
                        analyticsProgress: 0,
                        verifySummary,
                        error: isDup ? `${section.title}: ${t('rstg.err.dupName')}` : `${section.title}: ${t('rstg.err.prefix')} (ErrorCode ${code})${rawDetail ? ': ' + rawDetail : ''}`,
                    });
                    return;
                }

                results[section.id] = {
                    reportName: this.buildReportName(),
                    module: section.module,
                    data: res.Data,
                };
            }

            this.setState({
                progress: 100,
                verifyPhase: VERIFY_PASSED,
                analyticsPhase: ANALYTICS_RUNNING,
                analyticsProgress: 22,
                message: t('rstg.msg.verifyDone'),
                results,
            });

            await wait(400);
            this.setState({ analyticsProgress: 55, message: t('rstg.msg.analyzing') });
            await wait(400);
            this.setState({ analyticsProgress: 82, message: t('rstg.msg.finishing') });
            await wait(320);

            this.setState(
                {
                    loading: false,
                    flow: FLOW_ANALYTICS,
                    analyticsPhase: ANALYTICS_DONE,
                    analyticsProgress: 100,
                    message: t('rstg.msg.done'),
                    sections: initSectionsState(),
                    progress: 0,
                },
                this.scrollToTop,
            );
        } catch (err) {
            this.setState({
                loading: false,
                verifyPhase: VERIFY_FAILED_UPLOAD,
                analyticsPhase: ANALYTICS_IDLE,
                analyticsProgress: 0,
                error: err.message || t('rstg.err.unknown'),
            });
        } finally {
            this.uploadInFlight = false;
        }
    };

    getProcessState() {
        const { verifyPhase, analyticsPhase, progress, verifySummary, analyticsProgress } = this.state;

        if (verifyPhase === VERIFY_FAILED_UPLOAD) {
            return {
                percent: Math.max(5, Math.min(progress || 35, 90)),
                title: t('rstg.process.uploadFailed'),
                desc: t('rstg.process.uploadFailedDesc'),
                tone: 'failed',
            };
        }

        if (verifyPhase === VERIFY_FAILED_VALIDATION) {
            const otherPart = verifySummary && verifySummary.other ? t('rstg.process.validateOther').replace('{n}', verifySummary.other) : '';
            const summary = verifySummary ? t('rstg.process.validateSummary').replace('{missing}', verifySummary.missingFields).replace('{wrong}', verifySummary.wrongFormat).replace('{other}', otherPart) : t('rstg.process.schemaInvalid');
            return {
                percent: 100,
                title: t('rstg.process.validateFailed'),
                desc: summary,
                tone: 'failed',
            };
        }

        if (verifyPhase === VERIFY_UPLOADING) {
            const p = Math.max(5, Math.min(Math.round((progress || 0) * 0.75), 72));
            return {
                percent: p,
                title: t('rstg.process.uploading'),
                desc: t('rstg.process.uploadingDesc'),
                tone: 'running',
            };
        }

        if (verifyPhase === VERIFY_VALIDATING) {
            return {
                percent: 85,
                title: t('rstg.process.validating'),
                desc: t('rstg.process.validatingDesc'),
                tone: 'running',
            };
        }

        if (verifyPhase === VERIFY_PASSED && analyticsPhase === ANALYTICS_RUNNING) {
            return {
                percent: analyticsProgress,
                title: t('rstg.process.analyzing'),
                desc: t('rstg.process.analyzingDesc'),
                tone: 'running',
            };
        }

        if (analyticsPhase === ANALYTICS_DONE) {
            return {
                percent: 100,
                title: t('rstg.process.done'),
                desc: t('rstg.process.doneDesc'),
                tone: 'done',
            };
        }

        return {
            percent: 0,
            title: t('rstg.process.ready'),
            desc: t('rstg.process.readyDesc'),
            tone: 'idle',
        };
    }

    renderFlowProcess() {
        const process = this.getProcessState();
        return (
            <div className={`rstg-process-unified ${process.tone}`}>
                <div className='rstg-process-unified-head'>
                    <div>
                        <div className='rstg-process-unified-title'>{process.title}</div>
                        <div className='rstg-process-unified-desc'>{process.desc}</div>
                    </div>
                    <div className='rstg-process-unified-percent'>{process.percent}%</div>
                </div>
                <div className='rstg-process-unified-track'>
                    <div className='rstg-process-unified-fill' style={{ width: `${process.percent}%` }} />
                </div>
            </div>
        );
    }

    isFlowFailed = () => {
        const { verifyPhase } = this.state;
        return verifyPhase === VERIFY_FAILED_UPLOAD || verifyPhase === VERIFY_FAILED_VALIDATION;
    };

    // Bước hiện tại của luồng hợp nhất: 0 Upload → 1 Checking → 2 Analyzing → 3 Output.
    getFlowStepIndex = () => {
        const { verifyPhase, analyticsPhase } = this.state;
        if (analyticsPhase === ANALYTICS_DONE) return 3;
        if (analyticsPhase === ANALYTICS_RUNNING || verifyPhase === VERIFY_PASSED) return 2;
        if (verifyPhase === VERIFY_VALIDATING || verifyPhase === VERIFY_FAILED_VALIDATION) return 1;
        return 0;
    };

    renderStepper() {
        const steps = [
            { key: 'upload', label: t('rstg.step.upload'), icon: 'pe-7s-cloud-upload' },
            { key: 'check', label: t('rstg.step.check'), icon: 'pe-7s-shield' },
            { key: 'analyze', label: t('rstg.step.analyze'), icon: 'pe-7s-graph1' },
            { key: 'output', label: t('rstg.step.output'), icon: 'pe-7s-note2' },
        ];
        const { processVisible, analyticsPhase } = this.state;
        const hasStarted = processVisible;
        const allDone = analyticsPhase === ANALYTICS_DONE;
        const active = this.getFlowStepIndex();
        const failed = this.isFlowFailed();

        return (
            <div className='rstg-stepper'>
                {steps.map((s, i) => {
                    let state = 'disabled';
                    if (hasStarted) {
                        state = 'todo';
                        if (allDone && i <= active) state = 'done';
                        else if (i < active) state = 'done';
                        else if (i === active) state = failed ? 'error' : 'active';
                    }
                    return (
                        <div key={s.key} className={`rstg-step is-${state}`}>
                            {i < steps.length - 1 && <span className='rstg-step-line' />}
                            <div className='rstg-step-dot'>
                                <i className={state === 'done' ? 'pe-7s-check' : state === 'error' ? 'pe-7s-close' : s.icon} />
                            </div>
                            <div className='rstg-step-meta'>
                                <span className='rstg-step-idx'>
                                    {t('rstg.step.prefix')} {i + 1}
                                </span>
                                <span className='rstg-step-label'>{s.label}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // RAROC là MỘT bộ dữ liệu: gộp toàn bộ sheet của CAP + CR vào 1 danh sách kiểm tra.
    getUnifiedFileItems = () => {
        const items = [];
        SECTION_CONFIGS.forEach((section) => {
            const st = this.state.sections[section.id];
            if (st.file) {
                items.push({ type: 'excel', sectionId: section.id, tag: section.tag, title: section.title, file: st.file });
            } else {
                section.sheets.forEach((sheet) => {
                    items.push({ type: 'sheet', sectionId: section.id, tag: section.tag, sheet, file: st.csvFiles[sheet] || null });
                });
            }
        });
        return items;
    };

    getUnifiedReadyCount = () => {
        const items = this.getUnifiedFileItems();
        const ready = items.filter((it) => !!it.file).length;
        return { ready, total: items.length };
    };

    renderUnifiedFiles() {
        const items = this.getUnifiedFileItems();
        const { ready, total } = this.getUnifiedReadyCount();
        const pct = total > 0 ? Math.round((ready / total) * 100) : 0;
        const hasUnassigned = SECTION_CONFIGS.some((s) => (this.state.sections[s.id].unassigned || []).length > 0);

        return (
            <div className='rstg-files-panel'>
                <div className='rstg-files-head'>
                    <div className='rstg-files-head-left'>
                        <div className='rstg-files-title'>{t('rstg.files.title')}</div>
                        <div className='rstg-files-sub'>{t('rstg.files.sub')}</div>
                    </div>
                    <div className='rstg-files-progress'>
                        <span className={'rstg-files-count' + (ready === total && total > 0 ? ' ok' : '')}>
                            {ready}/{total} files
                        </span>
                        <div className='rstg-files-track'>
                            <div className='rstg-files-fill' style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                </div>

                <div className='rstg-checklist'>
                    {items.map((item) => {
                        if (item.type === 'excel') {
                            return (
                                <div key={`excel_${item.sectionId}`} className='rstg-check-row ok'>
                                    <span className='rstg-check-icon'>
                                        <i className='pe-7s-check' />
                                    </span>
                                    <span className={`rstg-file-tag tag-${item.tag.toLowerCase()}`}>{item.tag}</span>
                                    <span className='rstg-check-name'>
                                        <b>
                                            {t('rstg.files.excelPkg')} · {item.title}
                                        </b>
                                        <small>
                                            {item.file.name} · {fmtSize(item.file.size)}
                                        </small>
                                    </span>
                                    <button type='button' className='rstg-x' title={t('rstg.files.remove')} onClick={() => this.removeExcel(item.sectionId)}>
                                        ×
                                    </button>
                                </div>
                            );
                        }
                        const f = item.file;
                        return (
                            <div key={`${item.sectionId}_${item.sheet}`} className={'rstg-check-row ' + (f ? 'ok' : 'miss')}>
                                <span className='rstg-check-icon'>
                                    <i className={f ? 'pe-7s-check' : 'pe-7s-attention'} />
                                </span>
                                <span className={`rstg-file-tag tag-${item.tag.toLowerCase()}`}>{item.tag}</span>
                                <span className='rstg-check-name'>
                                    <b>{item.sheet}</b>
                                    <small>{f ? `${f.name} · ${fmtSize(f.size)}` : t('rstg.csv.noFile')}</small>
                                </span>
                                {f ? (
                                    <button type='button' className='rstg-x' title={t('rstg.files.remove')} onClick={() => this.removeSheet(item.sectionId, item.sheet)}>
                                        ×
                                    </button>
                                ) : (
                                    <label className='rstg-mini-pick'>
                                        {t('rstg.csv.choose')}
                                        <input type='file' accept='.csv,.txt' onChange={(e) => this.onPickSlot(item.sectionId, item.sheet, e)} />
                                    </label>
                                )}
                            </div>
                        );
                    })}
                </div>

                {hasUnassigned && (
                    <div className='rstg-unassigned'>
                        <span className='rstg-label'>{t('rstg.files.unassignedLabel')}</span>
                        {SECTION_CONFIGS.map((section) => {
                            const st = this.state.sections[section.id];
                            if (st.file || !st.unassigned.length) return null;
                            return st.unassigned.map((f, idx) => (
                                <div key={`${section.id}_ua_${idx}`} className='rstg-ua-row'>
                                    <span className={`rstg-file-tag tag-${section.tag.toLowerCase()}`}>{section.tag}</span>
                                    <span className='rstg-ua-name'>
                                        {f.name} · {fmtSize(f.size)}
                                    </span>
                                    <select className='rstg-select' defaultValue='' onChange={(e) => this.assignUnassigned(section.id, idx, e.target.value)}>
                                        <option value='' disabled>
                                            {t('rstg.files.assignSheet')}
                                        </option>
                                        {section.sheets.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                    <button type='button' className='rstg-x' title={t('rstg.csv.discard')} onClick={() => this.removeUnassigned(section.id, idx)}>
                                        ×
                                    </button>
                                </div>
                            ));
                        })}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { flow, reportDate, datasetName, loading, progress, analyticsPhase, processVisible, results, message, error } = this.state;
        const allReady = SECTION_CONFIGS.every((section) => this.isSectionReady(section.id));
        const { ready, total } = this.getUnifiedReadyCount();

        return (
            <div className='content'>
                <div className='container-fluid rstg'>
                    {loading && <Loading />}

                    <div className='rstg-title'>
                        {t('rstg.upload.title')}
                        <button type='button' className='rstg-btn' onClick={this.downloadSample} disabled={this.state.downloadingSample}>
                            <i className='pe-7s-download' /> {this.state.downloadingSample ? t('rstg.btn.downloading') : t('rstg.btn.downloadSample')}
                        </button>
                    </div>

                    {this.renderStepper()}

                    {flow === FLOW_VERIFY && (
                        <div className='rstg-card'>
                            <div className='rstg-block rstg-row2' style={{ marginTop: 0 }}>
                                <div>
                                    <span className='rstg-label'>{t('rstg.field.datasetName')}</span>
                                    <input
                                        className='rstg-input'
                                        type='text'
                                        value={datasetName}
                                        placeholder='RAROC_2026Q2_MAIN_BATCH'
                                        onChange={(e) => this.setState({ datasetName: e.target.value })}
                                        onBlur={(e) => this.setState({ datasetName: ensureDatasetPrefix(e.target.value) })}
                                    />
                                </div>
                                <div>
                                    <span className='rstg-label'>{t('rstg.field.reportDate')}</span>
                                    <input className='rstg-input' type='date' value={reportDate} onChange={(e) => this.setState({ reportDate: day(e.target.value) })} />
                                </div>
                            </div>

                            <div className='rstg-block'>
                                <label className='rstg-dropzone' onDrop={this.onDropAllFiles} onDragOver={(e) => e.preventDefault()}>
                                    <span className='rstg-dz-icon'>
                                        <i className='pe-7s-cloud-upload' />
                                    </span>
                                    <span className='rstg-dz-main'>{t('rstg.dz.dropAll')}</span>
                                    <span className='rstg-dz-sub'>{t('rstg.dz.dropAllSub')}</span>
                                    <span className={'rstg-dz-count' + (ready === total && total > 0 ? ' ok' : '')}>
                                        {ready}/{total} {t('rstg.dz.filesReady')}
                                    </span>
                                    <input type='file' accept={ACCEPT} multiple onChange={this.onPickAllFiles} />
                                </label>
                            </div>

                            <div className='rstg-block' style={{ marginTop: 18 }}>
                                {this.renderUnifiedFiles()}
                            </div>

                            {message && (
                                <div className='rstg-alert rstg-alert--ok'>
                                    <i className='pe-7s-check' /> {message}
                                </div>
                            )}
                            {error && (
                                <div className='rstg-alert rstg-alert--err'>
                                    <i className='pe-7s-attention' /> {error}
                                </div>
                            )}

                            <div className='rstg-block rstg-upload-bar'>
                                <div className='rstg-upload-hint'>
                                    {allReady ? (
                                        <span className='ok'>
                                            <i className='pe-7s-check' /> {t('rstg.upload.allReady').replace('{n}', total)}
                                        </span>
                                    ) : (
                                        <span>
                                            <i className='pe-7s-info' /> {t('rstg.upload.stillMissing').replace('{n}', total - ready)}
                                        </span>
                                    )}
                                </div>
                                <button className='rstg-upload' disabled={loading || !allReady} onClick={this.onUpload}>
                                    {loading ? `${t('rstg.btn.processing')} ${progress}%` : t('rstg.btn.uploadAnalyze')}
                                </button>
                            </div>

                            <div className='rstg-block' style={{ marginTop: 14 }}>
                                {processVisible && this.renderFlowProcess()}
                            </div>
                        </div>
                    )}

                    {flow === FLOW_ANALYTICS && (
                        <div className='rstg-card'>
                            <div className='rstg-section-title' style={{ marginBottom: 14 }}>
                                {t('rstg.result.title')}
                            </div>
                            {Object.keys(results).length > 0 ? (
                                <div>
                                    {analyticsPhase === ANALYTICS_DONE && (
                                        <div className='rstg-alert rstg-alert--ok'>
                                            <i className='pe-7s-check' /> {message || t('rstg.msg.success')}
                                        </div>
                                    )}
                                    <div className='rstg-analytics-meta'>
                                        <span>
                                            <b>{t('rstg.meta.dataset')}:</b> {datasetName}
                                        </span>
                                        <span>
                                            <b>{t('rstg.field.reportDate')}:</b> {reportDate}
                                        </span>
                                        <button type='button' className='rstg-analytics-new' onClick={this.startNewUploadFlow}>
                                            {t('rstg.btn.newDataset')}
                                        </button>
                                    </div>

                                    <div className='rstg-block' style={{ marginTop: 12 }}>
                                        <RarocCrResultTable result={results.rarocCr && results.rarocCr.data} capResult={results.capMrCor && results.capMrCor.data} stgCcr={results.rarocCr && results.rarocCr.data && results.rarocCr.data.stg_ccr} />
                                    </div>
                                </div>
                            ) : (
                                <div className='rstg-alert rstg-alert--warn'>
                                    <i className='pe-7s-info' /> {t('rstg.result.empty')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// src/pages/Raroc/UploadFile/index.js